import { createFileRoute } from "@tanstack/react-router";
import { StickyHeader } from "@/components/site/StickyHeader";
import { Footer } from "@/components/site/Footer";
import { FadeIn } from "@/components/site/FadeIn";
import { EmailCaptureForm } from "@/components/site/EmailCaptureForm";

const CALENDLY = "https://calendly.com/katie-fernands/discovery-call";

export const Route = createFileRoute("/newsletter")({
  head: () => ({
    meta: [
      { title: "Notes on Referrals and Visibility — Katie Fernands" },
      {
        name: "description",
        content:
          "Join the list for financial professionals: practical ideas on LinkedIn visibility, warm outreach, and turning existing relationships into referrals.",
      },
      { property: "og:title", content: "Notes on Referrals and Visibility — Katie Fernands" },
      {
        property: "og:description",
        content:
          "Practical ideas on LinkedIn visibility, warm outreach, and turning existing relationships into referrals.",
      },
      { property: "og:url", content: "https://katiefernands.com/newsletter" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { rel: "canonical", content: "https://katiefernands.com/newsletter" },
    ],
    links: [{ rel: "canonical", href: "https://katiefernands.com/newsletter" }],
  }),
  component: NewsletterPage,
});

const topics = [
  "One LinkedIn profile change that makes you easier to find in referral conversations.",
  "Warm outreach templates that respect compliance and don't feel like cold pitches.",
  "How to turn a single client win into a month of content.",
  "The small-systems view of referral marketing for RIA firms and small banks.",
];

function NewsletterPage() {
  return (
    <div className="bg-background text-foreground min-h-screen flex flex-col">
      <StickyHeader />
      <main className="flex-1 pt-32 pb-24">
        <div className="mx-auto max-w-2xl px-6 lg:px-10">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.24em] text-primary mb-3">Newsletter</p>
            <h1 className="text-4xl md:text-5xl font-serif tracking-tight">
              Notes on referrals and visibility
            </h1>
            <p className="mt-5 text-foreground/70 text-lg leading-relaxed">
              A short email every other week for financial professionals who want to build a
              referral engine, not just chase the next lead. No trends, no hype. Just practical
              ideas you can use.
            </p>
          </FadeIn>

          <FadeIn>
            <ul className="mt-10 space-y-4">
              {topics.map((topic, i) => (
                <li key={i} className="flex gap-3 text-sm text-foreground/80">
                  <span className="text-primary">{i + 1}.</span>
                  <span>{topic}</span>
                </li>
              ))}
            </ul>
          </FadeIn>

          <FadeIn>
            <div className="mt-14">
              <EmailCaptureForm
                source="newsletter-page"
                variant="full"
                heading="Join the list"
                subheading="Enter your email. Unsubscribe anytime."
                buttonLabel="Join the list"
                successMessage="You're in. Look for the first note from me soon."
              />
            </div>
          </FadeIn>

          <FadeIn>
            <p className="mt-10 text-sm text-muted-foreground">
              Prefer to talk first?{" "}
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Book a short call here
              </a>{" "}
              and we'll figure out whether it makes sense to work together.
            </p>
          </FadeIn>
        </div>
      </main>
      <Footer />
    </div>
  );
}
